import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { PackageCreateModal } from "@/components/packages/PackageCreateModal";
import { PackageDrawer } from "@/components/packages/PackageDrawer";
import { PackagesFilters, type PackagesFiltersState } from "@/components/packages/PackagesFilters";
import { PackagesTable } from "@/components/packages/PackagesTable";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/lib/hooks/auth";
import { usePackagesList } from "@/lib/hooks/packages";
import { isReadOnlyRole, ROLE_LEVEL, type UserRole } from "@/lib/schemas/users";
import type { PackagesListFilters } from "@/lib/schemas/packages";

const packagesSearchSchema = z.object({
  // `highlight` = deep-link from global search palette (ADR-0091).
  highlight: z.string().optional(),
  // `search` seed (backlog 2.3): truncate hint in the palette links here.
  search: z.string().optional(),
});

export const Route = createFileRoute("/_app/packages")({
  component: PackagesPage,
  validateSearch: packagesSearchSchema,
});

const DEFAULT_FILTERS: PackagesFiltersState = {
  search: "",
  status: "all",
};

function PackagesPage(): JSX.Element {
  const { t } = useTranslation();
  const { highlight, search: searchFromUrl } = Route.useSearch();
  const [filters, setFilters] = useState<PackagesFiltersState>(() =>
    searchFromUrl ? { ...DEFAULT_FILTERS, search: searchFromUrl } : DEFAULT_FILTERS,
  );
  const [selectedId, setSelectedId] = useState<string | undefined>(highlight);
  const [cursor, setCursor] = useState<string | undefined>();
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (highlight) setSelectedId(highlight);
  }, [highlight]);

  useEffect(() => {
    if (searchFromUrl) {
      setFilters((prev) => ({ ...prev, search: searchFromUrl }));
      setCursor(undefined);
    }
  }, [searchFromUrl]);

  // Phase 10F: solo customer+ (level 1) puo' creare un package. peer_read
  // vede la lista in sola lettura. Mirror del BE _can_write_package() che
  // accetta tutti i ruoli tranne peer_read.
  const me = useCurrentUser();
  const myRole = (me.data?.role ?? "peer_read") as UserRole;
  const myLevel = ROLE_LEVEL[myRole] ?? 0;
  // Track B (ADR-0104): a reseller/account-admin has app_users.role='member'
  // (level 0). It creates packages for its customers, so allow it via the
  // account-admin capability too. The BE enforces per-department authz.
  const isAccountAdmin = me.data?.is_account_admin === true;
  const canCreate = (myLevel >= ROLE_LEVEL.customer || isAccountAdmin) && !isReadOnlyRole(myRole);

  const apiFilters: PackagesListFilters = useMemo(() => {
    const base: PackagesListFilters = { limit: 25 };
    if (filters.search) base.search = filters.search;
    if (filters.status !== "all") base.status = filters.status;
    if (cursor) base.cursor = cursor;
    return base;
  }, [filters, cursor]);

  const query = usePackagesList(apiFilters);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{t("nav.packages")}</h1>
          {query.data?.total_estimate !== undefined && (
            <p className="text-sm text-muted-foreground">
              {t("packages.list.total", { count: query.data.total_estimate })}
            </p>
          )}
        </div>
        {canCreate && (
          <Button
            type="button"
            onClick={() => setCreateOpen(true)}
            size="sm"
            className="gap-1.5"
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("packages.create.button")}
          </Button>
        )}
      </header>

      <PackageCreateModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => setSelectedId(id)}
      />

      <PackagesFilters
        value={filters}
        onChange={(next) => {
          setFilters(next);
          setCursor(undefined);
        }}
      />

      {query.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("packages.list.load_error")} — {query.error.message}
        </div>
      )}

      <PackagesTable
        packages={query.data?.data ?? []}
        isLoading={query.isLoading}
        selectedId={selectedId}
        onRowClick={(p) => setSelectedId(p.id)}
      />

      {query.data && (query.data.next_cursor || query.data.prev_cursor) && (
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!query.data.prev_cursor}
            onClick={() => setCursor(query.data.prev_cursor ?? undefined)}
          >
            ← {t("common.back")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!query.data.next_cursor}
            onClick={() => setCursor(query.data.next_cursor ?? undefined)}
          >
            {t("common.next")} →
          </Button>
        </div>
      )}

      <PackageDrawer packageId={selectedId} onClose={() => setSelectedId(undefined)} />
    </div>
  );
}
